// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { PhoneNumberUtil } from 'google-libphonenumber'
import Joi from 'joi'

import {
  Driver,
  EntityConstants,
  DriverDetailsPhonePattern,
  DigitPattern,
  AlphanumericAndSpacePattern,
  DEFAULT_VAT_CATEGORY_ID,
  DEFAULT_CURRENCY_ID,
} from 'constants/index'
import { DriverType, PaymentTerm } from 'generated/graphql'

const messages = {
  'string.base': 'Field must be a string',
  'string.email': 'Invalid email format',
  'string.max': `Field must be at most {#limit} characters`,
  'string.pattern.base': 'Please enter a valid phone number',
  'number.base': 'Field must be a number',
  'number.min': 'Value must be greater than or equal to {#limit}',
}

function validationSchema(
  vatCategoryId: string,
  currencyId: string,
  driverXeroCodeOptions?: Array<{ label: string; value: string; vatCategoryId: number }>,
) {
  const defaultVatCategoryId = vatCategoryId === DEFAULT_VAT_CATEGORY_ID
  const defaultCurrencyId = currencyId === DEFAULT_CURRENCY_ID

  const validateXeroPurchaseCode = (value: string, helpers: any) => {
    if (!value || !driverXeroCodeOptions) {
      return value
    }

    const currentVatCategoryId = helpers.state.ancestors[0]?.vatCategoryId
    if (!currentVatCategoryId) {
      return value
    }

    const isValidForVatCategory = driverXeroCodeOptions.some(
      (option) =>
        option.value === value && String(option.vatCategoryId) === String(currentVatCategoryId),
    )

    if (!isValidForVatCategory) {
      return helpers.error('xeroPurchaseCode.invalidForVatCategory', {
        xeroPurchaseCode: value,
        vatCategoryId: currentVatCategoryId,
      })
    }

    return value
  }

  return Joi.object({
    name: Joi.string()
      .required()
      .max(EntityConstants.MAX_NAME_LENGTH)
      .messages(messages)
      .label('Name'),
    firstName: Joi.string()
      .required()
      .max(EntityConstants.MAX_FIRST_NAME_LENGTH)
      .messages(messages)
      .label('First Name'),
    lastName: Joi.string()
      .required()
      .max(EntityConstants.MAX_FIRST_NAME_LENGTH)
      .messages(messages)
      .label('Last Name'),
    swift: Joi.string().allow(null, '').optional(),
    xeroCodeId: Joi.number().allow(null, '').optional(),
    xeroPurchaseCode: Joi.string()
      .when('$isAllowedToViewXeroPurchaseCodes', {
        is: true,
        then: Joi.required(),
        otherwise: Joi.optional(),
      })
      .trim()
      .disallow('', null)
      .pattern(new RegExp(DigitPattern))
      .custom(validateXeroPurchaseCode)
      .messages({
        'string.base': 'Mandatory field',
        'string.pattern.base': 'Only digits are allowed',
        'any.required': 'Mandatory field',
        'string.empty': 'Mandatory field',
        'any.invalid': 'Mandatory field',
        'xeroPurchaseCode.invalidForVatCategory':
          "Purchase code '{#xeroPurchaseCode}' is not valid for the selected VAT category. Please select a valid purchase code from the available options.",
      }),
    addressLine1: Joi.string()
      .required()
      .max(EntityConstants.MAX_ADDRESS_LENGTH)
      .messages(messages)
      .label('Address 1'),
    addressLine2: Joi.string()
      .max(EntityConstants.MAX_ADDRESS_LENGTH)
      .allow(null, '')
      .messages(messages),
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .max(EntityConstants.MAX_ADDRESS_LENGTH)
      .messages(messages)
      .label('Email'),
    invoiceEmail: Joi.string()
      .email({ tlds: { allow: false } })
      .optional()
      .allow('', null)
      .max(EntityConstants.MAX_ADDRESS_LENGTH)
      .messages(messages),
    driverType: Joi.string()
      .valid(...Object.values(DriverType))
      .required()
      .messages(messages),
    city: Joi.string()
      .required()
      .max(EntityConstants.MAX_CITY_LENGTH)
      .messages(messages)
      .label('City'),
    postcode: Joi.string()
      .required()
      .max(EntityConstants.MAX_POSTCODE_LENGTH)
      .messages(messages)
      .label('Post Code'),
    telephoneNumber: Joi.string()
      .required()
      .custom((value, helpers) => {
        const phoneUtil = PhoneNumberUtil.getInstance()
        try {
          const isValid = phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(value))
          if (!isValid) {
            return helpers.error('string.pattern.base')
          }
        } catch (error) {
          console.error('Error validating phone number:', error)
          return helpers.error('string.pattern.base')
        }
        return value
      })
      .pattern(new RegExp(DriverDetailsPhonePattern))
      .messages(messages)
      .label('Telephone Number'),
    startDate: Joi.date().required().messages(messages),

    currencyId: Joi.number().allow(null, '').min(0).messages(messages).label('Currency'),
    alternativeTelephoneNumber: Joi.string()
      .allow(null, '')
      .optional()
      .custom((value, helpers) => {
        if (!value || value.trim() === '' || /^\+\d{1,4}$/.test(value.trim())) {
          return ''
        }

        const phonePattern = new RegExp(DriverDetailsPhonePattern)
        if (!phonePattern.test(value)) {
          return helpers.error('string.pattern.base')
        }

        const phoneUtil = PhoneNumberUtil.getInstance()
        try {
          const isValid = phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(value))
          if (!isValid) {
            return helpers.error('string.pattern.base')
          }
        } catch (error) {
          console.error('Error validating alternative phone number:', error)
          return helpers.error('string.pattern.base')
        }
        return value
      })
      .messages(messages)
      .label('Alternative Telephone Number'),
    startDate: Joi.date().required(),
    countryId: Joi.number().allow(null, '').min(1).required().messages({
      'number.min': 'Country is a required field',
      'any.required': 'Country is a required field',
    }),
    paymentTerm: Joi.string()
      .valid(...Object.values(PaymentTerm))
      .required(),
    callSign: Joi.string().max(Driver.MAX_CALL_SIGN_LENGTH).allow(null, ''),
    courierExchangeMemberId: Joi.string().max(Driver.MAX_CALL_SIGN_LENGTH).allow(null, ''),
    postCode: Joi.string().max(EntityConstants.MAX_POSTCODE_LENGTH),
    isOnboardingCompleted: Joi.boolean().optional().allow(null),
    bankName: Joi.string()
      .max(Driver.MAX_BANK_NAME_LENGTH)
      .optional()
      .allow('', null)
      .messages({
        'string.max': `Bank Name must be at most ${Driver.MAX_BANK_NAME_LENGTH} characters`,
      }),

    bankAccountNumber: defaultCurrencyId
      ? Joi.string()
          .required()
          .trim()
          .disallow('', null)
          .length(Driver.MAX_BANK_ACCOUNT_NUMBER_LENGTH)
          .pattern(new RegExp(DigitPattern))
          .messages({
            'string.base': 'Bank Account Number must be a string',
            'string.empty': 'Bank Account Number is required',
            'any.required': 'Bank Account Number is required',
            'any.invalid': 'Bank Account Number is required',
            'string.length': `Bank Account Number must contain ${Driver.MAX_BANK_ACCOUNT_NUMBER_LENGTH} characters`,
            'string.pattern.base': 'Only digits are allowed.',
          })
      : Joi.string()
          .trim()
          .optional()
          .allow('', null)
          .length(Driver.MAX_BANK_ACCOUNT_NUMBER_LENGTH)
          .pattern(new RegExp(DigitPattern))
          .messages({
            'string.length': `Bank Account Number must contain ${Driver.MAX_BANK_ACCOUNT_NUMBER_LENGTH} characters`,
            'string.pattern.base': 'Only digits are allowed.',
          }),

    bankAccountHolderName: defaultCurrencyId
      ? Joi.string()
          .required()
          .trim()
          .disallow('', null)
          .max(Driver.MAX_BANK_ACCOUNT_HOLDER_NAME_LENGTH)
          .pattern(new RegExp(AlphanumericAndSpacePattern))
          .messages({
            'string.base': 'Bank Account Holder Name must be a string',
            'string.empty': 'Bank Account Holder Name is required',
            'any.required': 'Bank Account Holder Name is required',
            'any.invalid': 'Bank Account Holder Name is required',
            'string.max': `Bank Account Holder Name must be at most ${Driver.MAX_BANK_ACCOUNT_HOLDER_NAME_LENGTH} characters`,
            'string.pattern.base':
              'Only alphanumeric characters, spaces and dashes are allowed. Numeric characters are forbidden.',
          })
      : Joi.string()
          .trim()
          .optional()
          .allow('', null)
          .max(Driver.MAX_BANK_ACCOUNT_HOLDER_NAME_LENGTH)
          .pattern(new RegExp(AlphanumericAndSpacePattern))
          .messages({
            'string.max': `Bank Account Holder Name must be at most ${Driver.MAX_BANK_ACCOUNT_HOLDER_NAME_LENGTH} characters`,
            'string.pattern.base':
              'Only alphanumeric characters, spaces and dashes are allowed. Numeric characters are forbidden.',
          }),

    internationalBankAccountNumber: Joi.string()
      .max(Driver.MAX_BANK_INTERNATIONAL_ACCOUNT_NUMBER_LENGTH)
      .optional()
      .allow('', null)
      .messages({
        'string.max': `IBAN must be at most ${Driver.MAX_BANK_INTERNATIONAL_ACCOUNT_NUMBER_LENGTH} characters`,
      }),

    bankSortCode: defaultCurrencyId
      ? Joi.string()
          .required()
          .trim()
          .disallow('', null)
          .length(EntityConstants.MAX_BANK_SORT_CODE_LENGTH)
          .pattern(new RegExp(DigitPattern))
          .messages({
            'string.base': 'Bank Sort Code must be a string',
            'string.empty': 'Bank Sort Code is required',
            'any.required': 'Bank Sort Code is required',
            'any.invalid': 'Bank Sort Code is required',
            'string.length': `Bank sort code must contain ${EntityConstants.MAX_BANK_SORT_CODE_LENGTH} characters`,
            'string.pattern.base': 'Only digits are allowed',
          })
      : Joi.string()
          .trim()
          .optional()
          .allow('', null)
          .length(EntityConstants.MAX_BANK_SORT_CODE_LENGTH)
          .pattern(new RegExp(DigitPattern))
          .messages({
            'string.length': `Bank sort code must contain ${EntityConstants.MAX_BANK_SORT_CODE_LENGTH} characters`,
            'string.pattern.base': 'Only digits are allowed',
          }),

    vatCategoryId: Joi.string().required().label('Vat category'),

    vatIdNumber: defaultVatCategoryId
      ? Joi.string()
          .trim()
          .optional()
          .allow('', null)
          .max(EntityConstants.MAX_VAT_ID_NUMBER_LENGTH)
          .messages({
            'string.max': `Vat Reg. Num must be at most ${EntityConstants.MAX_VAT_ID_NUMBER_LENGTH} characters`,
          })
      : Joi.string()
          .required()
          .trim()
          .disallow('', null)
          .max(EntityConstants.MAX_VAT_ID_NUMBER_LENGTH)
          .messages({
            'string.base': 'Vat Reg. Num must be a string',
            'string.empty': 'Vat Reg. Num is required',
            'any.required': 'Vat Reg. Num is required',
            'any.invalid': 'Vat Reg. Num is required',
            'string.max': `Vat Reg. Num must be at most ${EntityConstants.MAX_VAT_ID_NUMBER_LENGTH} characters`,
          }),

    isActive: Joi.boolean().allow('', null).optional(),
    isEmailNotification: Joi.boolean().optional().allow(null),
    isDropCharge: Joi.boolean().allow('', null).optional(),
    eoriNumber: Joi.string().allow('', null).optional(),

    publicLiabilityExpirationDate: Joi.date().allow('', null).optional().messages(messages),

    companyRegistrationNumber: Joi.string()
      .max(EntityConstants.MAX_GENERIC_TEXT_LENGTH)
      .optional()
      .allow('', null)
      .messages({
        'string.max': `Company Registration Number must be at most ${EntityConstants.MAX_GENERIC_TEXT_LENGTH} characters`,
      }),
  })
}

export default validationSchema
